/**
 * La plantilla de una campaña, en página (`campanas/:id/plantilla?editor=`). Puro.
 *
 * El asistente y la página del editor se hablan sólo por la URL y por `location.state`: el asistente
 * abre la página con `?editor=` y la página vuelve al asistente con un aviso o un error. Las rutas y
 * los textos viven acá para que los dos lados armen y lean EXACTAMENTE lo mismo: si uno arma
 * `plantilla:<id>` y el otro espera otra cosa, el editor abre una plantilla nueva en silencio.
 */

const B = "/activa/oficial";
const enc = encodeURIComponent;

export const AVISO_YA_ENVIADA = "Esa plantilla ya se mandó a Meta: para cambiarla, duplicala";
export const AVISO_NO_EXISTE = "Esa plantilla ya no existe";
export const AVISO_MANDADA = "Mandada a Meta. Mientras la revisan, podés completar las variables y elegir cuándo sale.";

/**
 * Lo que la página del editor le deja al asistente en `location.state` al volver: el aviso o el error
 * que tiene que mostrar. Un tipo y no dos claves escritas a mano en cada lado: si una cambia de
 * nombre, el compilador lo ve en los dos.
 */
export type VueltaDelEditor = { avisoPlantilla?: string; errorPlantilla?: string };

/** Qué abre la página según `?editor=`: una nueva, la galería de modelos, un modelo o una plantilla existente. */
export type EditorPedido = { tipo: "nueva" } | { tipo: "modelos" } | { tipo: "modelo"; clave: string } | { tipo: "plantilla"; id: string };

/**
 * Lee `?editor=`. `null` si no hay o no se entiende: quien llama decide qué hacer (la página lo toma
 * como `nueva`). `modelo:` y `plantilla:` sin nada detrás también son `null`: abrir un modelo o una
 * plantilla sin clave terminaría en una nueva vacía que el operador no pidió.
 */
export function leerEditor(v: string | null): EditorPedido | null {
  if (!v) return null;
  if (v === "nueva") return { tipo: "nueva" };
  if (v === "modelos") return { tipo: "modelos" };
  if (v.startsWith("modelo:") && v.length > 7) return { tipo: "modelo", clave: v.slice(7) };
  if (v.startsWith("plantilla:") && v.length > 10) return { tipo: "plantilla", id: v.slice(10) };
  return null;
}

/**
 * `?publico=` de la ida y de la vuelta: el público recién creado desde el asistente («Guardar y volver a
 * la campaña») que todavía no se guardó en la campaña. Viaja en todo el recorrido (R-B12): si el viaje
 * al editor lo perdiera, el asistente volvería con el público viejo elegido y la campaña podría salir a
 * otra gente sin que nadie lo note.
 */
const conPublico = (publico?: string) => (publico ? `&publico=${enc(publico)}` : "");

/** La página del editor para una campaña. `valor` es lo que va en `?editor=` (`nueva`, `modelos`, `modelo:<clave>`, `plantilla:<id>`). */
export function rutaPlantillaDeCampana(idCampana: string, valor: string, publico?: string): string {
  return `${B}/campanas/${enc(idCampana)}/plantilla?editor=${enc(valor)}${conPublico(publico)}`;
}

/**
 * El asistente en el paso de la plantilla («Qué les decís»). Con `plantillaId` la deja elegida aunque la campaña no la
 * tenga asociada (la asociación falló al volver del editor y el operador tiene que verla igual).
 */
export function rutaVolverAlAsistente(idCampana: string, plantillaId?: string, publico?: string): string {
  return `${B}/campanas/${enc(idCampana)}/editar?paso=mensaje${plantillaId ? `&plantilla=${enc(plantillaId)}` : ""}${conPublico(publico)}`;
}
